import { useGeckoNotifications } from 'features/notifications'
import { useSageService } from 'features/sage'
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react'
import { v4 } from 'uuid'

export interface LlmState {
  isLoading: boolean
  sendMessage: (message: string) => void
}

export type UseLlmProps = {
  setMessages: Dispatch<SetStateAction<ReadonlyArray<ChatMessage>>>
  messages: ReadonlyArray<ChatMessage>
  handleResponse: (
    response: string,
    addMessage: (message: string) => void
  ) => boolean
  systemPrompt?: string
}

export interface ChatMessage {
  id: string
  content: string
  role: 'user' | 'assistant'
  timestamp: string
}

const buildQuery = (
  systemPrompt: string | undefined,
  messages: readonly ChatMessage[],
  userMessage: string
) =>
  (systemPrompt
    ? `---------------
      System prompt:
      ${systemPrompt}
      ---------------`
    : '') +
  `\nPast messages:
      ${messages.map((msg) => `${msg.role}: ${msg.content}`).join('\n')}
      ---------------
      Current message:
      User: ${userMessage}`

export function useLlm({
  setMessages,
  handleResponse,
  systemPrompt,
  messages,
}: UseLlmProps): LlmState {
  const { fetchVertexQuery } = useSageService()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { errorNotification } = useGeckoNotifications()
  const addMessage = useCallback(
    (content: string, role: 'user' | 'assistant') => {
      const newMessage: ChatMessage = {
        id: v4(),
        content,
        role,
        timestamp: new Date().toLocaleTimeString(),
      }
      setMessages((prev) => [...prev, newMessage])
    },
    [setMessages]
  )
  const sendMessage = useCallback(
    async (message: string) => {
      setIsLoading(true)
      try {
        addMessage(message, 'user')
        const response = await fetchVertexQuery({
          query: buildQuery(systemPrompt, messages, message),
        })
        const result = response.result
        if (handleResponse(result, (msg) => addMessage(msg, 'assistant'))) {
          addMessage(result, 'assistant')
        }
      } catch (error) {
        console.error(error)
        errorNotification(
          'Encountered an error reaching the AI. Please try again later.'
        )
        addMessage(
          'Sorry, I encountered an error. Please try again.',
          'assistant'
        )
      } finally {
        setIsLoading(false)
      }
    },
    [
      addMessage,
      errorNotification,
      fetchVertexQuery,
      handleResponse,
      messages,
      systemPrompt,
    ]
  )

  return useMemo(
    () => ({
      isLoading,
      sendMessage,
    }),
    [isLoading, sendMessage]
  )
}
